import { TType } from '../../hooks/useTranslation';
import { defaultRenderer, textColumn } from '../ProjectListTable/utils';
import { getTranslatedValue } from '../ApprovalPageTable/utils';
import { GridRenderCellParams } from '@mui/x-data-grid';
import InputField from '@ingka/input-field';
import React, { useEffect, useState } from 'react';
import Select from '@ingka/select';
import Option from '@ingka/select/Option';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import FormControl from '@mui/material/FormControl';
import FormField from '@ingka/form-field';
import Badge from '@ingka/badge';
import {
  getOptionsFromTranslation,
  returnAllowedStatuses,
} from '../../utils/utils';
import { validateNumber } from '../../common/CustomNumberInput/utils';
import { NumericFormat } from 'react-number-format';
import { GridColDef } from '@mui/x-data-grid-premium';
import skapaColours from '@ingka/variables/colours-css';
import CDSComboBox from '../CDSComboBox/CDSComboBox';
import plus from '@ingka/ssr-icon/paths/plus';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import { Stack } from '@mui/material';
import HelperText from '@ingka/helper-text';
import { ESTABLISHMENT_TYPE } from '../ProjectTypeSelect/utils';
import PartOfCrpComboBox from '../PartOfCrpComboBox';
import ApprovedCrpComboBox from '../ApprovedCrpComboBox';
import { AD, SA } from '../RoleRestricted/utils';

export const NumberInputRenderer = (
  params: GridRenderCellParams,
  onChange,
  disabled,
  disableIfNoValue = false,
  currencySymbol = 'Eur',
) => {
  const [focused, setFocused] = React.useState(false);
  const completeOnchange = (value) =>
    onChange(params.row.id, params.field, value);
  const disabledComplete =
    disabled ||
    (disableIfNoValue &&
      !(params?.value !== null && params?.value !== undefined));

  return (
    <NumericFormat
      value={focused ? params?.value : params?.value || 0}
      thousandSeparator
      // suffix={currencySymbol}
      id={`${params?.id}-input-params.field`}
      type={'text'}
      className={`short-dt-inputs ${disabledComplete && 'read-only-input'}`}
      data-testid={`${params?.id}-input-${params.field}`}
      onFocus={(e) => {
        if (disabledComplete) return;
        setFocused(true);
        if (!params?.value || params?.value === '0') {
          e.target.value = '';
          completeOnchange('');
        }
      }}
      onBlur={() => {
        if (disabledComplete) return;
        setFocused(false);
        if (params?.value === '' || isNaN(Number(params?.value))) {
          completeOnchange(0);
        }
      }}
      readOnly={disabledComplete}
      customInput={InputField}
      suffixLabel={currencySymbol}
      onValueChange={({ value }) => completeOnchange(validateNumber(value))}
    />
  );
};
export const requiredFields = [
  'countryProjectResponsible',
  'targetProjectStartDate',
  'targetProjectEndDate',
];
const handleKeyDown = (event) => {
  if (event.code === 'Space') {
    event.stopPropagation();
  }
};
export const inputRenderer = (
  params,
  onChange,
  disabled = false,
  required = false,
  customValidation = null,
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [localValue, setLocalValue] = useState(params?.value);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setLocalValue(params?.value);
  }, [params?.value]);

  return (
    <FormField
      valid={false}
      shouldValidate={
        (customValidation &&
          params?.api?.state?.customParams?.validationTriggered &&
          customValidation(params?.value)) ||
        ((requiredFields.includes(params?.field) || required) &&
          params?.api?.state?.customParams?.validationTriggered &&
          (params?.value === '' || params?.value === null))
      }
      style={{ marginBottom: '0' }}
    >
      <InputField
        id={`${params?.id}-input-params.field`}
        type={'text'}
        disabled={
          disabled || (params.field === 'unifierId' && !params?.row?.isep)
        }
        className={'short-dt-inputs'}
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
        }}
        onBlur={() => {
          onChange(params.row.id, params.field, localValue);
        }}
        onKeyDown={handleKeyDown}
      />
    </FormField>
  );
};

const firstSolutionTooltip = (solution, t) => (
  <Tooltip
    tooltipText={t(`solutionTypes.${solution.solutionType}`)}
    describedById={solution.id + '-solution-tooltip'}
    position={'bottom'}
  >
    <Badge
      label={truncateString(t(`solutionTypes.${solution.solutionType}`), 10)}
    />
  </Tooltip>
);

const truncateString = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const solutionsRenderer = (params, onAddSolution, t: TType) => {
  const solutions = Array.isArray(params.value) ? params.value : [];
  return (
    <Stack sx={{ position: 'absolute' }} spacing={1} direction={'row'}>
      {!!solutions.length && firstSolutionTooltip(solutions[0], t)}
      {solutions.length > 1 && (
        <Tooltip
          tooltipText={solutions
            .slice(1)
            .map((s) => t(`solutionTypes.${s.solutionType}`))
            .join(', ')}
          describedById={'rest-solution-tooltip'}
          position={'bottom'}
        >
          <Badge label={`+${solutions.length - 1}`} />
        </Tooltip>
      )}
      <Button
        type={'tertiary'}
        size={'xsmall'}
        onClick={() => onAddSolution(params.row.id)}
        ssrIcon={plus}
        iconPosition={'leading'}
        text={'Add'}
      />
      {params.api.state?.customParams?.validationTriggered &&
        !solutions.length && (
          <HelperText
            id="grid-validation-helper-id"
            shouldValidate={true}
            valid={false}
          >
            Missing solution
          </HelperText>
        )}
    </Stack>
  );
};

export const selectRenderer = (params, optionsArray, onChange, disabled) => (
  <Select
    id={`${params?.id}-select`}
    label={null}
    className={'skappa-utility-text-overflow-select'}
    style={{ width: '100%' }}
    disabled={disabled}
    value={params?.value}
    onChange={(e) => {
      onChange(params.row.id, params.field, e.target.value);
    }}
  >
    {Array.isArray(optionsArray) ? (
      <>
        {optionsArray.map((opt, index) => (
          <Option key={opt + Math.random() * index} value={opt} name={opt} />
        ))}
      </>
    ) : (
      <>
        {Object.keys(optionsArray).map((opt, index) => (
          <Option
            key={opt + Math.random() * index}
            value={opt}
            name={optionsArray[opt]}
          />
        ))}
      </>
    )}
  </Select>
);

const dateRenderer = (params, onChange, disabled, required = false) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <FormControl sx={{ background: 'white', borderColor: 'red' }}>
      <DesktopDatePicker
        sx={{
          fieldset: {
            borderColor:
              (requiredFields.includes(params?.field) || required) &&
              params?.api?.state?.customParams?.validationTriggered &&
              !params?.value
                ? `${skapaColours.colourCommercialMessageBtiRed} !important`
                : '',
          },
        }}
        disabled={disabled}
        label={null}
        onChange={(value) => {
          onChange(params.row.id, params.field, value);
        }}
        value={(params.value ? new Date(params.value) : null) as never}
        slotProps={{
          field: { clearable: true },
        }}
        format="yyyy/MM/dd"
      />
    </FormControl>
  </LocalizationProvider>
);

export const inputColunm = (
  field: string,
  headerName: string,
  onChange,
  disabled = false,
  minWidth = 250,
  valueGetter?: (value, row) => string,
  customClassName = '',
  required = false,
  customValidation = null,
) => {
  return {
    field,
    headerName,
    minWidth,
    display: 'flex',
    headerClassName: customClassName,
    disabled,
    valueGetter: (value, row) =>
      valueGetter ? valueGetter(value, row) : value,
    renderCell: (params) => {
      return inputRenderer(
        params,
        onChange,
        disabled,
        required,
        customValidation,
      );
    },
  } as GridColDef<any, any, any>;
};
export const numberColumn = (
  field: string,
  headerName: string,
  onChange,
  disabled = false,
  minWidth = 250,
  valueGetter?: (value, row, column?: unknown, params?: unknown) => string,
  customClassName = null,
  disableIfNoValue = false,
  error = false,
  currencySymbol = '',
  cellClassName = null,
) => {
  return {
    field,
    headerName,
    cellClassName: (params) =>
      (cellClassName && cellClassName(params)) ||
      params?.row[`${field.split('-')[0]}-error`]
        ? error && 'number-column-error'
        : '',
    minWidth,
    display: 'flex',
    headerClassName: customClassName,
    disabled,
    valueGetter: (value, row, column, params) =>
      valueGetter ? valueGetter(value, row, column, params) : value,
    renderCell: (params) =>
      NumberInputRenderer(
        params,
        onChange,
        disabled || isBudgetInputDisabled(params),
        disableIfNoValue,
        currencySymbol,
      ),
  };
};

const isBudgetInputDisabled = (params): boolean => {
  if (params?.row?.establishmentType === 'CLOSE') {
    return true;
  }

  if (params?.row?.realEstateSISAN) {
    return params?.row?.budgetType !== 'RELEASED_APPROVED_BUDGET';
  }

  const disabledBudgetTypes = ['ACTUALS', 'BASELINE', 'SUBMITTED_FORECAST'];

  if (params?.row?.isep) {
    return [...disabledBudgetTypes, 'BUDGET'].includes(params?.row?.budgetType);
  }

  return [
    ...disabledBudgetTypes,
    'FORECAST',
    'RELEASED_APPROVED_BUDGET',
  ].includes(params?.row?.budgetType);
};

export const selectColumn = (
  field: string,
  headerName: string,
  optionsArray,
  onchange,
  disabled?: boolean,
  customClassName = null,
) => {
  return {
    field,
    headerName,
    type: 'singleSelect',
    aggregable: false,
    display: 'flex',
    minWidth: 250,
    cellClassName: customClassName,
    renderCell: (params) =>
      selectRenderer(params, optionsArray, onchange, disabled),
  } as GridColDef<any, any, any>;
};

export const StatusColumn = (onchange, role, t, disabled?: boolean) => {
  return {
    field: 'projectStatus',
    headerName: t('ppm.columns.projectStatus'),
    type: 'select',
    aggregable: false,
    display: 'flex',
    minWidth: 200,
    renderCell: (params) => {
      return selectRenderer(
        params,
        params?.row ? returnAllowedStatuses([params.row], role) : [],
        onchange,
        disabled,
      );
    },
  };
};

export const dateColumn = (
  field: string,
  headerName: string,
  onChange,
  disabled = false,
  required = false,
) => {
  return {
    field,
    headerName,
    aggregable: false,
    display: 'flex',
    minWidth: 250,
    renderCell: (params) => dateRenderer(params, onChange, disabled, required),
  } as GridColDef<any, any, any>;
};

export const getTableColumns = (
  t: TType,
  onChange,
  userRole,
  closeProject = false,
  userOptions,
  onAddSolutionsClicked,
) => {
  const columns = [
    textColumn('referenceId', 'Reference ID'),
    textColumn('businessUnitId', 'BU'),
    textColumn('projectName', t('ppm.columns.projectName'), 300),
    {
      field: 'unitType',
      headerName: t('ppm.columns.unitType'),
      aggregable: false,
      minWidth: 230,
      display: 'flex',
      valueGetter: (value) => getTranslatedValue(value, 'unitTypes', t),
      renderCell: defaultRenderer,
    },
    {
      field: 'solutions',
      headerName: 'Solution',
      aggregable: false,
      display: 'flex',
      minWidth: 250,
      renderCell: (params) => {
        if (
          ![
            ESTABLISHMENT_TYPE.IMPROVE,
            ESTABLISHMENT_TYPE.FORMAT_CHANGE,
          ].includes(params.row.establishmentType)
        )
          return '';
        return solutionsRenderer(params, onAddSolutionsClicked, t);
      },
    },
    StatusColumn(onChange, userRole, t),
    {
      field: 'partOfCrpPlan',
      headerName: t('common.partOfCRPPlan'),
      display: 'flex',
      minWidth: 300,
      cellClassName: 'overflow-visible',
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
          <PartOfCrpComboBox
            id={'partOfCrp-' + params.row.id}
            label=""
            maxDisplayItems={1}
            partOfCrp={params.row.partOfCrp}
            handleUpdate={(value) =>
              onChange(params.row.id, 'partOfCrp', value)
            }
          />
        </div>
      ),
    },
    {
      field: 'partOfApprovedCrp',
      headerName: t('common.partOfApprovedCRP'),
      display: 'flex',
      minWidth: 300,
      cellClassName: 'overflow-visible',
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
          <ApprovedCrpComboBox
            id={'approvedCrp-' + params.row.id}
            label=""
            maxDisplayItems={1}
            partOfCrp={params.row.partOfCrp}
            handleUpdate={(value) =>
              onChange(params.row.id, 'partOfCrp', value)
            }
            disabled={![AD, SA].includes(userRole)}
          />
        </div>
      ),
    },
    dateColumn(
      'projectStatusDate',
      t('labels.projectStatusDate'),
      onChange,
      true,
    ),
    inputColunm('unifierId', t('labels.unifierId'), onChange),
    selectColumn(
      'etrProjectPrioritization',
      t('labels.projectPrioritisation'),
      getOptionsFromTranslation(t, 'projectPrioritization'),
      onChange,
    ),
    dateColumn(
      'targetProjectStartDate',
      t('ppm.columns.startDate') + '*',
      onChange,
    ),
    dateColumn(
      'targetProjectEndDate',
      t('ppm.columns.openingDate') + '*',
      onChange,
    ),
    inputColunm('projectManager', t('labels.projectManager'), onChange),
    {
      field: 'countryProjectResponsible',
      headerName: 'Country Project Responsible',
      display: 'flex',
      minWidth: 500,
      renderCell: (params) => (
        <CDSComboBox
          additionalStyles={{ background: 'white' }}
          value={params.value}
          onChange={(value) =>
            onChange(params.row.id, 'countryProjectResponsible', value)
          }
          id={'countryProjectResponsible' + params.row.id}
          options={userOptions}
          error={
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            params?.api?.state?.customParams?.validationTriggered &&
            (params?.value === '' || params?.value === null)
          }
        />
      ),
    },
    inputColunm('countryProjectComments', 'Country Project Comments', onChange),
  ];
  if (closeProject) {
    return [
      ...columns,
      {
        field: 'estimatedNetEffert',
        headerName: t('labels.estimatedNetEffort'),
        minWidth: 250,
        display: 'flex',
        renderCell: (params) =>
          NumberInputRenderer(
            params,
            onChange,
            params?.row?.establishmentType !== 'CLOSE',
            false,
            '',
          ),
      },
    ];
  }
  return columns;
};

const DEFAULT_COLUMNS = [
  'referenceId',
  'businessUnitId',
  'projectName',
  'unitType',
  'budgetType',
];

export const setVisibleColumns = (apiRef, showEur) => {
  if (!apiRef?.current) return;
  const allFields = apiRef.current.getAllColumns().map((col) => col.field);
  const filtered = allFields.filter((c) =>
    DEFAULT_COLUMNS.includes(c) || !showEur
      ? !c?.includes('EUR')
      : c?.includes('EUR'),
  );
  const newModel = {};
  for (const field of allFields) {
    newModel[field] = filtered.includes(field);
  }
  apiRef.current.setColumnVisibilityModel(newModel);
};
